import React from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { ErrorScreen } from '@atlassian/generative-ai-modal/screens/Error';

import { updateAIAuditLogPluginState } from '../../../../pm-plugins/ai-audit-logs/commands';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import type { ErrorInfo } from '../../../utils/error-handling';
import { ErrorMessage, getStepName, retryEnabled } from '../../../utils/error-handling';
import { FAILURE_REASON } from '../../../utils/errors';

export function ErrorScreenWithLogic({ errorInfo }: { errorInfo: ErrorInfo }) {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const { endExperience, sendToAIExperienceMachine, editorView, appearance } =
		aiExperienceCommonData;
	const { isInDocumentStreamingEnabled, api } = useContentComponentContext();
	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);
	useAnalyticsFlowEventsQueue({
		stepName: getStepName(errorInfo),
		attributes: {
			errorType: errorInfo.failureReason ?? FAILURE_REASON.UNKNOWN,
			sawAupViolation: errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION,
			...errorInfo,
		},
	});

	React.useEffect(() => {
		if (fg('platform_editor_ai_audit_logs_events')) {
			if (errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION) {
				const command = updateAIAuditLogPluginState({
					'aup-violation': {
						eventStatus: 'send-event',
					},
				});
				command(editorView.state, editorView.dispatch);
			}
		}
	}, [errorInfo.failureReason, editorView]);

	if (nextSessionId && fg('platform_editor_ai_migrate_from_xstate')) {
		return null;
	}

	return (
		<ErrorScreen
			text={ErrorMessage(errorInfo)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCancel={() => {
				endExperience();
			}}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onRetry={() => sendToAIExperienceMachine({ type: 'retry' })}
			showRetryButton={retryEnabled(errorInfo)}
			appearance={appearance}
			isAIFC={isInDocumentStreamingEnabled}
		/>
	);
}
